import type { ReportDraft, ReportFinalExcel } from "./reportDraftApi";

export const REPORT_XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type DownloadOptions = {
  role: string | null;
  dirty: boolean;
  busy: boolean;
  queryError: boolean;
};

export function reportDownloadAccess(
  report: ReportDraft,
  latest: ReportDraft | undefined,
  options: DownloadOptions,
) {
  const allowed = ["ADMIN", "OPERATOR", "FINANCE"].includes(options.role ?? "");
  const approved =
    report.state === "Approved" &&
    report.version > 0 &&
    !!report.approval &&
    !!report.importInfo;
  const current =
    !!latest &&
    latest.orderListId === report.orderListId &&
    latest.version === report.version &&
    latest.state === "Approved" &&
    !!latest.approval &&
    !!latest.importInfo &&
    latest.sourceHash === report.sourceHash &&
    latest.liveSourceHash === report.liveSourceHash &&
    report.sourceHash === report.liveSourceHash &&
    !report.sourceChanged &&
    !latest.sourceChanged;
  const hasErrors = [report, latest].some((item) =>
    item?.issues.some((issue) => issue.severity === "error"),
  );
  const blockedReason = !allowed
    ? "Таны эрхээр эцсийн Excel татах боломжгүй."
    : !approved
      ? "Хянасан Excel-тэй тайланг баталсны дараа эцсийн файл татна."
      : options.queryError
        ? "Эхийг шалгаж чадсангүй. Дахин шалгасны дараа файл татна уу."
        : !current
          ? "Эх өгөгдөл эсвэл тайлангийн хувилбар өөрчлөгдсөн тул эцсийн Excel татах боломжгүй. Эхийг шалгаж, зөрүүг хянана уу."
          : hasErrors
            ? "Тайлангийн зөрчлийг шийдвэрлэсний дараа эцсийн Excel татна."
            : options.dirty
              ? "Хадгалаагүй өөрчлөлттэй үед эцсийн Excel татах боломжгүй."
              : options.busy
                ? "Одоогийн ажиллагаа дуусахыг хүлээнэ үү."
                : null;
  return { eligible: blockedReason === null, blockedReason };
}

export function reportDownloadContextKey(
  report: ReportDraft | null,
  latest: ReportDraft | undefined,
  dirty: boolean,
  queryError: boolean,
  role: string | null,
) {
  const identity = (item: ReportDraft | null | undefined) =>
    item && [
      item.orderListId,
      item.version,
      item.state,
      item.sourceHash,
      item.liveSourceHash,
      item.sourceChanged,
      item.approval,
      item.importInfo?.fileHash,
      item.issues.filter((issue) => issue.severity === "error"),
    ];
  return JSON.stringify([
    identity(report),
    identity(latest),
    dirty,
    queryError,
    role,
  ]);
}

// Invalidating a generation also covers a context change followed by a revert.
export function createReportDownloadGuard() {
  let context = "";
  let generation = 0;
  return {
    setContext(next: string) {
      if (next !== context) {
        context = next;
        generation++;
      }
    },
    invalidate() {
      generation++;
    },
    capture() {
      return generation;
    },
    isCurrent(token: number) {
      return token === generation;
    },
  };
}

class ReportDownloadError extends Error {
  name = "ReportDownloadError";
}

export function reportFinalExcelError(error: unknown): string {
  if (error instanceof ReportDownloadError) return error.message;
  const graphql = error as { response?: { errors?: { message?: string }[] } };
  return (
    graphql?.response?.errors
      ?.map((item) => item.message)
      .filter(Boolean)
      .join(". ") || "Эцсийн Excel үүсгэж чадсангүй. Дахин оролдоно уу."
  );
}

export async function prepareReportFinalExcel(
  file: ReportFinalExcel,
  expectedVersion: number,
): Promise<{ fileName: string; blob: Blob }> {
  if (file.reportVersion !== expectedVersion)
    throw new ReportDownloadError(
      "Файлын хувилбар тайлантай таарахгүй байна. Эхийг дахин шалгана уу.",
    );
  if (
    file.mimeType !== REPORT_XLSX_MIME ||
    !/^[^/\\\u0000-\u001f]+\.xlsx$/i.test(file.fileName) ||
    !file.fileBase64 ||
    file.fileBase64.length % 4 !== 0 ||
    !/^[A-Za-z0-9+/]+={0,2}$/.test(file.fileBase64)
  )
    throw new ReportDownloadError(
      "Excel файлын өгөгдөл буруу байна. Дахин татна уу.",
    );
  const binary = atob(file.fileBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++)
    bytes[index] = binary.charCodeAt(index);
  if (
    bytes.length < 4 ||
    bytes[0] !== 0x50 ||
    bytes[1] !== 0x4b ||
    bytes[2] !== 0x03 ||
    bytes[3] !== 0x04 ||
    !/^[a-f0-9]{64}$/i.test(file.sha256)
  )
    throw new ReportDownloadError("Excel файл бүрэн ирсэнгүй. Дахин татна уу.");
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  if (hash !== file.sha256.toLowerCase())
    throw new ReportDownloadError(
      "Excel файлын бүрэн бүтэн байдал таарахгүй байна. Дахин татна уу.",
    );
  return {
    fileName: file.fileName,
    blob: new Blob([bytes], { type: REPORT_XLSX_MIME }),
  };
}

export function saveReportFinalExcel(file: { fileName: string; blob: Blob }) {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement("a");
  try {
    anchor.href = url;
    anchor.download = file.fileName;
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    // Let the browser consume the download before releasing its backing bytes.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
